import type {AnyObject} from '@/types/Common'

/**
 * Extracts configuration value from rule attributes
 * @param rule - The validation rule object
 * @param path - Dot-notation path to the configuration value (e.g., 'config.params.value')
 * @returns The configuration value or undefined if not found
 */
export function extractConfigValue(rule: AnyObject, path: string): any {
  const configAttr = rule.attributes?.find((attr: AnyObject) => attr.name === 'config')
  if (!configAttr?.value) return undefined

  const pathParts = path.split('.')
  let current = configAttr.value

  for (const part of pathParts) {
    if (current && typeof current === 'object' && part in current) {
      current = current[part]
    } else {
      return undefined
    }
  }

  return current
}

/**
 * Extracts numeric configuration value with validation
 * @param rule - The validation rule object
 * @param path - Dot-notation path to the configuration value
 * @returns The numeric value or undefined if not found or not a number
 */
export function extractNumericConfig(rule: AnyObject, path: string): number | undefined {
  const value = extractConfigValue(rule, path)
  return typeof value === 'number' ? value : undefined
}

/**
 * Checks if a value is empty (null, undefined, empty string, empty array)
 * @param value - Value to check
 * @returns true if value is considered empty
 */
export function isEmpty(value: any): boolean {
  return value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0)
}

/**
 * Checks if a value is a valid string for validation
 * @param value - Value to check
 * @returns true if value is a non-empty string
 */
export function isValidString(value: any): boolean {
  return typeof value === 'string' && value.length > 0
}

/**
 * Checks if any of the validation rules is a required rule
 * @param rules - Array of validation functions
 * @param serverRules - Server-side validation rules for the attribute
 * @param validationRulesProps - Props containing server validation rules
 * @param attributeName - Name of the attribute to check
 * @returns true if field is required
 */
export function isFieldRequired(
  rules: Array<(value: any) => any> = [],
  serverRules: Array<(value: any) => any> = [],
  validationRulesProps?: Record<string, any[]>,
  attributeName?: string,
): boolean {
  for (const rule of rules) {
    const result = rule('')
    if (Array.isArray(result) && result[0] === 'validation.required') {
      return true
    }
  }
  
  for (const rule of serverRules) {
    const result = rule('')
    if (Array.isArray(result) && result[0] === 'validation.required') {
      return true
    }
  }
  
  if (validationRulesProps && attributeName && validationRulesProps[attributeName]) {
    const propRules = validationRulesProps[attributeName]
    if (Array.isArray(propRules)) {
      for (const rule of propRules) {
        if (rule && typeof rule === 'object' && rule.name === 'required') {
          return true
        }
      }
    }
  }
  
  return false
}

/**
 * Creates a label with required indicator if the field is required
 * @param labelText - The original label text
 * @param isRequired - Whether the field is required
 * @returns HTML string with label and optional red asterisk
 */
export function createRequiredLabel(labelText: string = '', isRequired: boolean = false): string {
  if (!labelText) return labelText
  
  if (isRequired) {
    return `${labelText} <span style="color: red;">*</span>`
  }
  
  return labelText
} 
